import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { fetchOperatingProfitSplitPostings } from '@/lib/api/operating-costs-admin.service'
import type { OperatingProfitSplitPostingsPage } from '@/types/api'
import {
  OPERATING_COSTS_LOAD_ERROR,
  resolveOperatingProfitSplitPostingsPage,
  resolveOperatingCostsError,
} from '../lib/operating-costs'
import type { SplitCommissionCostFilterValues, SplitCommissionCostPostingsController } from './useSplitCommissionCostPostings'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

const EMPTY_FILTERS: SplitCommissionCostFilterValues = {
  campaignId: '',
  institutionId: '',
  month: '',
  from: '',
  to: '',
}

export type LegalPrimeProfitPostingsController = SplitCommissionCostPostingsController

export function useLegalPrimeProfitPostings(): LegalPrimeProfitPostingsController {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const runWithStepUpRef = useRef(runWithStepUp)
  runWithStepUpRef.current = runWithStepUp
  const [data, setData] = useState<OperatingProfitSplitPostingsPage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draftFilters, setDraftFilters] = useState<SplitCommissionCostFilterValues>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<SplitCommissionCostFilterValues>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const loadControllerRef = useRef<AbortController | null>(null)

  const load = useCallback(async (filters: SplitCommissionCostFilterValues, pageNumber: number) => {
    loadControllerRef.current?.abort()
    const controller = new AbortController()
    loadControllerRef.current = controller
    setIsLoading(true)
    setError(null)
    try {
      const campaignId = Number(filters.campaignId)
      const institutionId = Number(filters.institutionId)
      const response = await runWithStepUpRef.current(() =>
        fetchOperatingProfitSplitPostings(
          {
            campaignId: Number.isInteger(campaignId) && campaignId > 0 ? campaignId : null,
            institutionId: Number.isInteger(institutionId) && institutionId > 0 ? institutionId : null,
            month: filters.month,
            from: filters.from,
            to: filters.to,
            page: pageNumber,
          },
          controller.signal
        )
      )
      if (controller.signal.aborted) return
      const resolved = resolveOperatingProfitSplitPostingsPage(response)
      if (!resolved) {
        setData(null)
        setError(OPERATING_COSTS_LOAD_ERROR)
        return
      }
      setData(resolved)
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) return
      setData(null)
      setError(resolveOperatingCostsError(err))
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void load(appliedFilters, page)
    return () => {
      loadControllerRef.current?.abort()
    }
  }, [appliedFilters, load, page])

  const applyFilters = useCallback(() => {
    setPage(1)
    setAppliedFilters(draftFilters)
  }, [draftFilters])

  const clearFilters = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setPage(1)
  }, [])

  const goToPage = useCallback((nextPage: number) => {
    if (nextPage < 1) return
    setPage(nextPage)
  }, [])

  return {
    data,
    isLoading,
    error,
    draftFilters,
    setDraftFilters,
    applyFilters,
    clearFilters,
    goToPage,
  }
}
